
import { MEDIA_QUERIES } from '../../../javascripts/constants';
import DOMObserver from '../../../javascripts/globals/domobserver';

/**
 * @typedef {object} FilterClasses
 * @property {string} element
 * @property {string} initializedModifier
 * @property {string} filterButton
 * @property {string} filterButtonText
 * @property {string} filterForm
 * @property {string} filterOpenModifier
 */

export default class Filter {
  /** @type {FilterClasses} */
  static DefaultClasses = {
    element: 'js-filter',
    initializedModifier: 'filter--initialized',
    filterButton: 'filter__button',
    filterButtonText: 'filter__button-text',
    filterForm: 'filter__form',
    filterOpenModifier: 'filter__form--open',
  };

  /** @type {HTMLElement} */
  $el;

  /** @type {FilterClasses} */
  classes;

  /** @type {string} */
  visibleFilterBreakPoint;

  /** @type {string} */
  filterButtonOpenText;

  /** @type {string} */
  filterButtonCloseText;

  constructor($el, classes) {
    this.$el = $el;
    this.classes = {
      ...Filter.DefaultClasses,
      ...classes,
    };
    this.visibleFilterBreakPoint = MEDIA_QUERIES.l;
    this.init().then(() => {
      this.$el.classList.add(this.classes.initializedModifier);
    });
  }

  async init() {
    const { $el, classes } = this;
    // Find filter's form element
    this.$filterForm = $el.querySelector(`.${classes.filterForm}`);
    const filterFormId = this.$filterForm.id;

    // Find filter's toggle button
    this.$filterButton = document.querySelector(`[aria-controls=${filterFormId}]`);

    if (this.$filterButton) {
      // Read ui text for toggle button state
      this.filterButtonOpenText = this.$filterButton.dataset.textOpen || 'Show filter';
      this.filterButtonCloseText = this.$filterButton.dataset.textClose || 'Hide filter';

      // Find the toggle button's text element
      this.$filterButtonText = this.$filterButton.querySelector(`.${classes.filterButtonText}`);

      // Add click event to the toggle button to show or hide the filter form
      this.$filterButton.addEventListener('click', () => {
        if (this.$filterButton.getAttribute('aria-expanded') === 'false') {
          this.showFilter();
        } else {
          this.hideFilter();
        }
      });

      // Decide whether the filter is visible by default or not. (Depends on screen size)
      const mq = window.matchMedia(this.visibleFilterBreakPoint);

      // Listen to viewport size changes
      mq.addEventListener('change', (e) => {
        if (e.matches) {
          this.showFilter();
        } else {
          this.hideFilter();
        }
      });

      // Set filter visibility initially
      if (mq.matches) {
        this.showFilter();
      }
    }
  }

  showFilter() {
    // Add the modifier class to the filter form
    this.$filterForm.classList.add(`${this.classes.filterOpenModifier}`);
    // Update toggle button's aria-attribute
    this.$filterButton.setAttribute('aria-expanded', 'true');
    // Update toggle button's text
    this.$filterButtonText.innerHTML = this.filterButtonCloseText;
  }

  hideFilter() {
    // Remove the modifier class from the filter form
    this.$filterForm.classList.remove(`${this.classes.filterOpenModifier}`);
    // Update toggle button's aria-attribute
    this.$filterButton.setAttribute('aria-expanded', 'false');
    // Update toggle button's text
    this.$filterButtonText.innerHTML = this.filterButtonOpenText;
  }

  static InitializeDefaults() {
    const { element, initializedModifier } = Filter.DefaultClasses;

    /*
    document
      .querySelectorAll(`.${element}:not(.${initializedModifier})`)
      .forEach($filter => new Filter($filter));
    */

    document
      .querySelectorAll(`.${element}:not(.${initializedModifier})`)
      .forEach(($filter) => {
        // eslint-disable-next-line no-new
        new Filter($filter);
        $filter.classList.add(Filter.DefaultClasses.initializedModifier);
      });
  }
}

const domObserver = new DOMObserver();
domObserver.watch(document.body, () => {
  Filter.InitializeDefaults();
});

Filter.InitializeDefaults();
